<template>
    <PageHeaderButtons :headerId="props.headerId"
        :buttons="buttons"
        :count="data.selectedItems?.length"
        :clearable="true"
        pluralizationCode="release"
        @clear="clearSelectedItems"
    />

    <div class="filters">
        <Field
            icon="search" sprite="actions"
            :placeholder="t('blocks.tracks.search')"
            class="noerror light"
            @change="onSearchChange"
        />

        <Filters
            :filters="filters"
            @change="setFilters"
        />

        <Sort :sort="TRACKS_SORT"
            :code="data.sort?.code"
            @change="setSort" />
    </div>

    <div class="tracks-list">
        <Explorer :items="data.items"
            :colCount="3"
            minItemWidth="260px"
            maxItemWidth="500px"
            @select="onSelectItems"
            ref="explorer"
        >
            <template v-slot="{ item, selected }">
                <TrackPreview :item="item" 
                    :showState="true"
                    :disabled="props.disabledItems?.indexOf(item.id) !== -1"
                    v-context-menu="contextMenu"
                />
            </template>
        </Explorer>

        <LoadMore
            :loading="data.loading"
            :values="data.items"
            :total="data.total"
            :take="data.take"
            pluralization="track"
            @click="getItems"
        />

        <EmptyLabel
            v-if="!data.items.length && !data.loading"
            class="empty"
            :title="t(`pages.tracks.empty.title`)"
            :description="t(`pages.tracks.empty.description`)"
        />
    </div>
</template>

<script setup>
    import { useI18n } from 'vue-i18n';
    import { onMounted, reactive, ref, computed } from 'vue';
    // import api from '🔗/api';
    import Sort from '🔗/components/ui/Sort.vue';
    import Filters from '🔗/components/ui/Filters.vue';
    import Button from '🔗/components/ui/Button.vue';
    import LoadMore from '🔗/components/ui/LoadMore.vue';
    import Explorer from '🔗/components/ui/Explorer.vue';
    import EmptyLabel from '🔗/components/ui/EmptyLabel.vue';
    import TrackPreview from '🔗/components/ui/TrackPreview.vue';
    import Field from '🔗/components/ui/Field.vue';
    import KebabMenu from '🔗/components/ui/KebabMenu.vue';

    import PageHeaderButtons from '@/components/blocks/PageHeader/PageHeaderButtons.vue';

    // import stores from '@/stores';

    import { TRACKS_SORT, TRACKS_FILTERS } from '🔗/constants';


    const emit = defineEmits(['select', 'add', 'licenses', 'edit']);
    const explorer = ref(null);

    const tracks = api.tracks();
    const user = stores.user();
    const modals = stores.modals();
    const locale = stores.locale();

    const { t } = useI18n();

    const props = defineProps({
        artist: { type: [Object, null], default: null },
        sort: { type: Array, default: () => ([]) },
        filters: { type: Array, default: () => ([]) },

        disabledItems: { type: Array, default: () => ([]) },

        canAdd: { type: Boolean, default: true },
        canRemove: { type: Boolean, default: true },
        canEdit: { type: Boolean, default: true },
        canEditLicense: { type: Boolean, default: true },
        headerId: { type: [String, null], default: '#item-head-buttons' }
    });

    const data = reactive({
        items: [],
        loading: false,
        skip: 0,
        take: 30,
        total: 0,
        sort: null,
        searchStr: null,
        filters: {},

        addTrackOptions: Array.from({ length: 18 }, (_, i) => ({
            code: i + 1,
            name: t('pluralization.tracks.add', { count: i + 1, track: t('pluralization.tracks.track', i + 1) })
        }))
    });

    const buttons = computed(() => {
        const result = [];

        if (!data.selectedItems?.length) {
            if (props.canAdd) {
                result.push({
                    type: 'kebab',
                    name: t(`pages.tracks.add.title`),
                    icon: 'plus',
                    sprite: 'actions',
                    disabled: !tracks.canAdd({ artist: props.artist }),
                    items: data.addTrackOptions,
                    onClick: (item) => emit('add', item.code)
                })
            }

            return result;
        }

        if (data.selectedItems.some(track => tracks.canRemove({
            artist: props.artist,
            track
        })) && props.canRemove) {
            result.push({
                type: 'button',
                title: data.selectedItems.length > 1 ? t(`pages.tracks.remove-multiple.title`) : t(`pages.tracks.remove.title`),
                icon: 'trash',
                sprite: 'actions',
                onClick: removeSelectedItems
            });
        }

        if (data.selectedItems.some(track => tracks.canEditLicense({
            artist: props.artist,
            track
        })) && props.canEditLicense) {
            result.push({
                type: 'button',
                name: data.selectedItems.length > 1 ? t(`pages.licenses.tracks.edit-multiple.title`) : t(`pages.licenses.tracks.edit.title`),
                icon: 'license',
                sprite: 'sections',
                onClick: editSelectedItemsLicenses
            });
        }

        if (data.selectedItems.some(track => tracks.canEdit({
                artist: props.artist,
                track
            }))) {
            result.push({
                type: 'button',
                name: data.selectedItems.length > 1 ? t(`pages.tracks.edit-multiple.title`) : t(`pages.tracks.edit.title`),
                icon: 'pen',
                sprite: 'actions',
                disabled: !props.canEdit,
                onClick: editSelectedItems
            });
        } else {
            result.push({
                type: 'button',
                name: data.selectedItems.length > 1 ? t(`pages.tracks.view-multiple.title`) : t(`pages.tracks.view.title`),
                icon: 'eye-opened',
                sprite: 'actions',
                disabled: !data.selectedItems.some(track => tracks.canView({
                    artist: props.artist,
                    track
                })) || !props.canEdit,
                onClick: editSelectedItems
            });
        }


        return result;
    });

    const contextMenu = computed(() => {
        return buttons.value.filter(item => item.type === 'button').map(item => ({
            ...item,
            name: item.name || item.title
        })).reverse();
    });

    const filters = computed(() => {
        const conditions = [];

        if (props.artist?.id) {
            conditions.push((item) => item.code !== 'contributors');
        }

        if (!conditions?.length) {
            return TRACKS_FILTERS
        }

        return TRACKS_FILTERS.filter(item => !!conditions.every(condition => condition(item)));
    });

    const setFilters = (payload) => {
        data.filters = payload;
        reload();
    }

    const setSort = (item) => {
        if (data.sort?.code === item.code) {
            data.sort = null;
        } else {
            data.sort = item;
        }

        reload();
    };

    const onSearchChange = async (e) => {
        data.searchStr = e.target.value?.toLowerCase().trim() || '';
        data.skip = 0;

        clearTimeout(data.searchTimeout);

        data.searchTimeout = setTimeout(() => {
            delete data.searchTimeout;
            reload();
        }, 200);
    }

    const getItems = async () => {
        data.loading = true;

        clearTimeout(data.setItemsTimeout);

        if (!data.skip) {
            data.items = [];
        }

        let params = {
            skip: data.skip,
            take: data.take
        };

        if (data.sort) {
            params.sort = data.sort.code;
            params.sort_dir = data.sort.direction;
        }

        if (!!data.searchStr?.trim().length) {
            params.title = data.searchStr.toLowerCase().trim();
        }

        if (data.filters && Object.keys(data.filters)?.length) {
            params = { ...data.filters, ...params };
        }

        if (props.artist) {
            params.contributors = [props.artist.id].join(',');
        }

        const payload = await api.tracks().list(params) || [];

        data.setItemsTimeout = setTimeout(() => {
            if (payload?.items?.length) {
                data.items.splice(data.items.length, 0, ...payload.items);
            }

            data.skip = data.items.length;
            data.total = payload?.total || 0;

            data.loading = false;
        }, 200);
    };

    const reload = () => {
        data.skip = 0;
        getItems();
    };

    const addItem = (item) => {
        if (!item?.id) {
            return;
        }

        data.items.unshift(item)
        data.skip += 1;
        data.total += 1;
    }

    const updateItem = (payload) => {
        if (!payload?.id) {
            return;
        }

        const item = data.items.find(item => item.id === payload.id);
        if (item) {
            data.items.splice(data.items.indexOf(item), 1, payload);
        }
    }

    const removeItems = async (items, happened) => {
        if (!items?.length) {
            return;
        }

        // Удаляем лишние
        if (!happened) {
            await Promise.all(items.map(async (item) => {
                await tracks.remove(item.id);
            }));
        }

        const ids = items.map(item => item.id);

        const oldlength = data.items.length;
        data.items = data.items.filter(item => ids.indexOf(item.id) === -1);

        if (oldlength - data.items?.length > 0) {
            data.skip -= oldlength - data.items?.length;
            data.total -= oldlength - data.items?.length;
        }
    }

    const onSelectItems = (items) => {
        data.selectedItems = items;
        emit('select', items)
    }

    const removeSelectedItems = async () => {
        if (!data.selectedItems?.length) {
            return;
        }

        modals.confirm({
            title: t(`pages.tracks.remove-multiple.confirm.title`),
            body: t(`pages.tracks.remove-multiple.confirm.description`),
            onOk: async () => {
                await removeItems(data.selectedItems.filter(track => tracks.canRemove({
                        artist: props.artist,
                        track
                    })));
                
                modals.open('success', {
                    data: {
                        title: t(`pages.tracks.remove-multiple.done.title`),
                        close: t(`pages.tracks.remove-multiple.done.close`)
                    },
                    quietClose: true
                });

                clearSelectedItems();
            }
        }); 
    }

    const editSelectedItems = () => {
        if (!data.selectedItems?.length) {
            return;
        }

        emit('edit', data.selectedItems);
    }

    const editSelectedItemsLicenses = () => {
        if (!data.selectedItems?.length) {
            return;
        }

        emit('licenses', data.selectedItems);
    }

    const clearSelectedItems = () => {
        explorer?.value?.clearSelection();
        delete data.selectedItems;
        emit('select')
    }

    defineExpose({
        reload,
        updateItem,
        removeItems,
        addItem
    });

    onMounted(() => {
        getItems();
    });
</script>

<style lang="scss" scoped>
    .tracks-list {
        display: flex;
        flex: 1 1;
        flex-direction: column;

        .loader {
            flex-shrink: 0;
            margin: auto;
        }

        button {
            flex-shrink: 0;
        }
    }

    .filters {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        justify-content: end;
        padding: 0 10px;
    }

    .selected {
        font: var(--font-caption);
        color: var(--white-500);
        min-width: 110px;
        text-align: right;
    }
</style>
